import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { TimeInterval } from '@birco/rainmesh-api';
import { AreaChartModule, LineChartModule } from '@swimlane/ngx-charts';
import { TranslatePipe } from '@ngx-translate/core';
import { curveNatural } from 'd3-shape';
import { WaterLevelChartSeriesPoint } from '../../models/water-level-chart-series-point.interface';
import { formatStatisticsTooltipDate } from '../../utils/format-statistics-tooltip-date';
import { LocaleService } from '../../../../translation/services/locale.service';
import { LocalizedNumberPipe } from '../../../../shared/pipes/localized-number.pipe';

const WATER_LEVEL_COLORS = ['#6EA8FE', '#003A8F'];

@Component({
  selector: 'app-chart-water-level',
  imports: [
    CommonModule,
    AreaChartModule,
    LineChartModule,
    TranslatePipe,
    LocalizedNumberPipe,
  ],
  templateUrl: './chart-water-level.component.html',
  styleUrl: './chart-water-level.component.scss',
})
export class ChartWaterLevelComponent {
  private readonly localeService = inject(LocaleService);
  private _sensorData: {
    name: string;
    series: WaterLevelChartSeriesPoint[];
  }[] = [];
  @Input() overlap = false;
  @Input() interval: TimeInterval | null = null;
  animations = false;
  protected legendData: { name: string; color: string }[] = [];

  @Input()
  set sensorData(
    value: { name: string; series: WaterLevelChartSeriesPoint[] }[],
  ) {
    this._sensorData = value.flatMap((series) => {
      const visiblePoints = series.series.flatMap((point, index, points) => {
        if (point.value != null) {
          return [point];
        }

        const previousPoint = points[index - 1];
        const nextPoint = points[index + 1];

        if (previousPoint?.value != null && nextPoint?.value != null) {
          return [
            {
              ...point,
              value: (previousPoint.value + nextPoint.value) / 2,
              absoluteLiters: null,
              missing: true,
            },
          ];
        }

        return [];
      });

      const axisValue =
        visiblePoints.find((point) => point.value != null)?.value ?? 0;

      return [
        {
          ...series,
          series: visiblePoints,
        },
        {
          name: '__axis__',
          series: series.series.map((point) => ({
            ...point,
            value: axisValue,
          })),
        },
      ];
    });

      this.legendData = value.map((series, index) => ({

    name: series.name,

    color: WATER_LEVEL_COLORS[index % WATER_LEVEL_COLORS.length],

  }));

    this.customColors = [
      ...value.map((series, index) => ({
        name: series.name,
        value: WATER_LEVEL_COLORS[index % WATER_LEVEL_COLORS.length],
      })),
      {
        name: '__axis__',
        value: 'transparent',
      },
    ];
  }

  get sensorData(): { name: string; series: WaterLevelChartSeriesPoint[] }[] {
    return this._sensorData;
  }

  curve = curveNatural;

  customColors: { name: string; value: string }[] = [];

  formatPercentage(value: number): string {
    return `${Math.round(value)} %`;
  }

  formatLabel(value: string): string {
    return value;
  }

  getTooltipPoint(
    model:
      | WaterLevelChartSeriesPoint
      | WaterLevelChartSeriesPoint[]
      | undefined,
  ): WaterLevelChartSeriesPoint | null {
    if (Array.isArray(model)) {
      return model[0] ?? null;
    }

    return model ?? null;
  }

  formatTooltipDate(point: WaterLevelChartSeriesPoint): string {
    return formatStatisticsTooltipDate(
      point.timestamp,
      this.interval,
      this.localeService.userLanguage,
    );
  }
}
